import type { SafetyCaseRecord } from '../../services/reportsApi';
import { externalSafetyReviewPath } from './externalReviewCopy';
import { sanitizeExternalLabel } from './externalReportLanguage';

export type ExternalReviewPackageListItem = {
  safetyCaseId: string;
  reportId: string;
  title: string;
  areaLabel: string;
  statusLabel: string;
  scanWindowLabel: string;
  reviewPath: string;
  isDemoOrLoopedSource: boolean;
};

function isDemoListItem(item: SafetyCaseRecord): boolean {
  if (item.is_demo_or_looped_source === true) return true;
  const blob = JSON.stringify(item).toLowerCase();
  return blob.includes('demo') || blob.includes('looped') || blob.includes('demosites');
}

function externalStatusLabel(raw: string, isDemo: boolean): string {
  const cleaned = sanitizeExternalLabel(raw, { isDemo });
  if (/\bvalidated\b/i.test(cleaned)) {
    return isDemo ? 'Signed Safety Scan ready' : 'Review package ready';
  }
  return cleaned || 'Review package ready';
}

function externalTitle(item: SafetyCaseRecord, isDemo: boolean): string {
  const candidates = [
    item.risk_label,
    item.risk_pattern_label,
    item.hazard_category,
    item.finding_type ? String(item.finding_type).replace(/_/g, ' ') : '',
  ];
  for (const candidate of candidates) {
    const cleaned = sanitizeExternalLabel(String(candidate || ''), { isDemo });
    if (cleaned && !/^(zone violation|zone_violation|restricted-zone exposure)$/i.test(cleaned)) {
      return cleaned;
    }
  }
  return isDemo
    ? 'Pedestrian exposure in forklift movement area'
    : 'Workplace condition observed';
}

function scanWindowLabel(item: SafetyCaseRecord): string {
  const scope = item.scope && typeof item.scope === 'object' ? item.scope : {};
  const timeWindow = item.time_window && typeof item.time_window === 'object' ? item.time_window : {};
  let from = String(
    (timeWindow as Record<string, unknown>).start
      || (scope as Record<string, unknown>).start
      || (scope as Record<string, unknown>).scope_start
      || '',
  ).trim();
  let to = String(
    (timeWindow as Record<string, unknown>).end
      || (scope as Record<string, unknown>).end
      || (scope as Record<string, unknown>).scope_end
      || '',
  ).trim();
  if (from && to && from > to) {
    [from, to] = [to, from];
  }
  if (from && to) return `${from} – ${to}`;
  if (from || to) return from || to;
  const signed = String(item.latest_scan_signed_at || item.report_finalized_at || '').trim();
  return signed ? `Signed ${signed}` : 'Scan window not specified';
}

export function normalizeSafetyCaseListResponse(data: {
  safety_cases?: SafetyCaseRecord[];
  items?: SafetyCaseRecord[];
}): SafetyCaseRecord[] {
  if (Array.isArray(data.safety_cases)) return data.safety_cases;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

/** Map selected-review Safety Cases to external package cards (finalized scan required). */
export function mapExternalReviewPackageList(cases: SafetyCaseRecord[]): ExternalReviewPackageListItem[] {
  const items: ExternalReviewPackageListItem[] = [];
  for (const item of cases) {
    const reportId = String(item.latest_finalized_report_id || '').trim();
    const safetyCaseId = String(item.safety_case_id || item.finding_id || '').trim();
    if (!reportId || !safetyCaseId) continue;
    const isDemo = isDemoListItem(item);
    const title = externalTitle(item, isDemo);
    items.push({
      safetyCaseId,
      reportId,
      title,
      areaLabel:
        sanitizeExternalLabel(
          String(item.area_label || item.zone_label || item.camera_label || ''),
          { isDemo },
        ) || (isDemo ? 'Demo forklift movement area' : 'Site area'),
      statusLabel: externalStatusLabel(
        String(item.safety_case_status_label || item.safety_case_status || item.status || ''),
        isDemo,
      ),
      scanWindowLabel: scanWindowLabel(item),
      reviewPath: externalSafetyReviewPath(reportId),
      isDemoOrLoopedSource: isDemo,
    });
  }
  return items;
}
